import { parseISO, addMinutes, addSeconds, format } from 'date-fns';
import { IProduct } from '@/redux/cart/types';
import { ISeat, IRacket } from '@/pages/Cart/types';

const TEST_PHONES = ['+79251590433', '+71111111111', '+79998360292'];

const RACKET_PRODUCT_IDS = {
  PADEL: '6f9edd25-b88b-11ef-80cd-ac1f6bea5f2d',
  TENNIS: '8a0acd69-d54e-11e5-8c48-382c4a64b65a',
};

const VAT_CODES = {
  COURT: 1,
  RACKET: 1,
  PRODUCT: 1,
};

export interface IRacketData {
  [courtId: string]: {
    racketServiceVAT?: number;
    courtType?: string;
  };
}

export interface IKassaItem {
  quantity: number;
  amount: {
    value: string;
    currency: 'RUB';
  };
  vat_code: number;
  description: string;
  paymentMethodType: 'full_prepayment';
}

export interface IBitrixItem {
  itemType: 'rent' | 'productCount' | 'product' | 'storage';
  courtName?: string;
  courtId?: string;
  productId?: string;
  sum: number;
  count?: number;
  startTime?: string;
  finishTime?: string;
  startDate?: string;
  finishDate?: string;
  gruntCourtId?: string;
  gruntStartTime?: string;
  gruntFinishTime?: string;
  gruntSum?: number;
}

export interface IStorageItem {
  month: number;
  year: number;
  monthName: string;
  price: number;
  count: number;
  startDate: string;
  endDate: string;
  storageItem: boolean;
}

export interface ICompareItemsResult {
  items: IKassaItem[] | IBitrixItem[];
  hasGruntReOrder: boolean;
  description: string;
  totalPrice: number;
}

export function testPayment(price: number, phone?: string): number {
  if (!phone) return price;
  
  const normalizedPhone = phone.replace(/\D/g, '');
  const testPhonesNormalized = TEST_PHONES.map(p => p.replace(/\D/g, ''));
  
  if (testPhonesNormalized.includes(normalizedPhone)) {
    return 1.0;
  }
  
  return price;
}

export function compareItems(
  forKassa: boolean,
  racketData: IRacketData,
  courtRent: { [key: string]: ISeat },
  racketRent: { [key: string]: IRacket },
  productsInBasket: { [key: string]: IProduct },
  phone?: string,
  storageRent: { [key: string]: IStorageItem } = {}
): ICompareItemsResult {
  const kassaItems: IKassaItem[] = [];
  const bitrixItems: IBitrixItem[] = [];
  const description: string[] = [];
  let hasGruntReOrder = false;
  let totalPrice = 0;

  for (const courtKey in courtRent) {
    const courtItem = courtRent[courtKey] as ISeat;
    const courtData = racketData[courtItem.courtId?.toString() || ''] || {};

    const bitrixCourtId = (courtItem as any).bitrixCourtId || courtItem.courtId?.toString() || '';
    
    const [hours, minutes] = courtItem.time.split(':').map(Number);
    const startDate = new Date(courtItem.date);
    startDate.setHours(hours, minutes, 0, 0);
    const startTimeISO = startDate.toISOString();
    
    const finishDate = new Date(startDate);
    finishDate.setMinutes(finishDate.getMinutes() + 59);
    finishDate.setSeconds(59);
    const finishTimeISO = finishDate.toISOString();
    
    const gruntCourtId = (courtItem as any).gruntCourtId;
    if (gruntCourtId) {
      hasGruntReOrder = true;

      const gruntStartTime = parseISO((courtItem as any).gruntStartTime || startTimeISO);
      const finishTimeGrunt = addSeconds(addMinutes(gruntStartTime, 59), 59);
      const finishTimeStringGrunt = format(finishTimeGrunt, "yyyy-MM-dd'T'HH:mm:ss");

      if (!forKassa) {
        const reorderItem: IBitrixItem = {
          itemType: "rent",
          courtName: courtItem.courtTitle || `Корт ${courtItem.courtId}`,
          courtId: bitrixCourtId,
          sum: courtItem.price,
          startTime: startTimeISO,
          finishTime: finishTimeISO,
          gruntCourtId: gruntCourtId,
          gruntStartTime: (courtItem as any).gruntStartTime,
          gruntFinishTime: finishTimeStringGrunt,
          gruntSum: (courtItem as any).gruntSum
        };

        bitrixItems.push(reorderItem);
      }
    } else {
      if (!forKassa) {
        bitrixItems.push({
          itemType: "rent",
          courtName: courtItem.courtTitle || `Корт ${courtItem.courtId}`,
          courtId: bitrixCourtId,
          sum: courtItem.price,
          startTime: startTimeISO,
          finishTime: finishTimeISO
        });
      } else {
        const descriptionTemp = `Услуга по аренде корта - ${courtItem.courtTitle || `Корт ${courtItem.courtId}`}`;
        const priceTemp = testPayment(courtItem.price, phone);
        const courtRentVAT = (courtItem as any).courtRentVAT || VAT_CODES.COURT;

        kassaItems.push({
          quantity: 1,
          amount: {
            value: parseFloat(priceTemp.toFixed(2)).toFixed(2),
            currency: 'RUB',
          },
          vat_code: courtRentVAT,
          description: descriptionTemp,
          paymentMethodType: 'full_prepayment',
        });

        description.push(descriptionTemp);
        totalPrice += priceTemp;
      }
    }
    
    for (const racketKey in racketRent) {
      const racket = racketRent[racketKey] as IRacket;
      
      if (racket.courtId === courtItem.courtId && 
          racket.date === courtItem.date && 
          racket.time === courtItem.time &&
          racket.count > 0) {
        
        if (!forKassa) {
          const [racketHours, racketMinutes] = racket.time.split(':').map(Number);
          const racketStartDate = new Date(racket.date);
          racketStartDate.setHours(racketHours, racketMinutes, 0, 0);
          const racketStartTimeISO = racketStartDate.toISOString();
          
          const racketFinishDate = new Date(racketStartDate);
          racketFinishDate.setMinutes(racketFinishDate.getMinutes() + 59);
          racketFinishDate.setSeconds(59);
          const racketFinishTimeISO = racketFinishDate.toISOString();

          const racketBitrixCourtId = (courtItem as any).bitrixCourtId || courtItem.courtId?.toString() || '';

          bitrixItems.push({
            itemType: "productCount",
            courtName: courtItem.courtTitle || `Корт ${courtItem.courtId}`,
            courtId: racketBitrixCourtId,
            productId: courtData.courtType === 'Padel'
              ? RACKET_PRODUCT_IDS.PADEL
              : RACKET_PRODUCT_IDS.TENNIS,
            sum: racket.price * racket.count,
            count: racket.count,
            startTime: racketStartTimeISO,
            finishTime: racketFinishTimeISO
          });
        } else {
          const descriptionTemp = `Услуга по аренде ракетки для игры в ${courtData.courtType === 'Padel' ? 'padel' : 'теннис'}`;
          const priceTemp = testPayment(racket.price, phone);
          const racketServiceVAT = courtData.racketServiceVAT || VAT_CODES.RACKET;

          kassaItems.push({
            quantity: racket.count,
            amount: {
              value: parseFloat(priceTemp.toFixed(2)).toFixed(2),
              currency: 'RUB',
            },
            vat_code: racketServiceVAT,
            description: descriptionTemp,
            paymentMethodType: 'full_prepayment',
          });

          description.push(descriptionTemp);
          totalPrice += priceTemp * racket.count;
        }
      }
    }
  }

  for (const racketKey in racketRent) {
    const racket = racketRent[racketKey] as IRacket;
    
    if (!racket.count || racket.count <= 0) {
      continue;
    }

    let isAttachedToCourt = false;
    for (const courtKey in courtRent) {
      const courtItem = courtRent[courtKey] as ISeat;
      if (racket.courtId === courtItem.courtId && 
          racket.date === courtItem.date && 
          racket.time === courtItem.time) {
        isAttachedToCourt = true;
        break;
      }
    }
    
    if (isAttachedToCourt) {
      continue;
    }

    const racketId = racket.title?.toLowerCase().includes('padel')
      ? RACKET_PRODUCT_IDS.PADEL
      : RACKET_PRODUCT_IDS.TENNIS;

    if (!forKassa) {
      const [racketHours, racketMinutes] = racket.time.split(':').map(Number);
      const racketStartDate = new Date(racket.date);
      racketStartDate.setHours(racketHours, racketMinutes, 0, 0);
      const racketStartTimeISO = racketStartDate.toISOString();
      
      const racketFinishDate = new Date(racketStartDate);
      racketFinishDate.setMinutes(racketFinishDate.getMinutes() + 59);
      racketFinishDate.setSeconds(59);
      const racketFinishTimeISO = racketFinishDate.toISOString();
      
      bitrixItems.push({
        itemType: "productCount",
        productId: racketId,
        sum: racket.price * racket.count,
        count: racket.count,
        startTime: racketStartTimeISO,
        finishTime: racketFinishTimeISO
      });
    } else {
      const descriptionTemp = `Услуга по аренде ракетки для игры в ${racket.title?.toLowerCase().includes('padel') ? 'padel' : 'теннис'}`;
      const priceTemp = testPayment(racket.price, phone);
      
      const racketServiceVAT = racketData[racket.courtId?.toString() || '']?.racketServiceVAT || VAT_CODES.RACKET;

      kassaItems.push({
        quantity: racket.count,
        amount: {
          value: parseFloat(priceTemp.toFixed(2)).toFixed(2),
          currency: 'RUB',
        },
        vat_code: racketServiceVAT,
        description: descriptionTemp,
        paymentMethodType: 'full_prepayment',
      });

      description.push(descriptionTemp);
      totalPrice += priceTemp * racket.count;
    }
  }

  for (const productKey in productsInBasket) {
    const product = productsInBasket[productKey] as IProduct;
    
    if (!forKassa) {
      bitrixItems.push({
        itemType: "product",
        productId: product.productId.toString(),
        sum: product.price * (product.count || 1),
        count: product.count || 1
      });
    } else {
      const isRental = product.productName?.toLowerCase().includes('полотенце');
      const descriptionTemp = `${isRental ? 'Аренда' : 'Продажа'} товара - ${product.productName}`;
      const priceTemp = testPayment(product.price, phone);

      kassaItems.push({
        quantity: product.count || 1,
        amount: {
          value: parseFloat(priceTemp.toFixed(2)).toFixed(2),
          currency: 'RUB',
        },
        vat_code: product.productVAT,
        description: descriptionTemp,
        paymentMethodType: 'full_prepayment',
      });

      description.push(descriptionTemp);
      totalPrice += priceTemp * (product.count || 1);
    }
  }

  const MONTH_NAMES = [
    'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
    'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
  ];

  for (const storageKey in storageRent) {
    const storage = storageRent[storageKey];
    if (!storage.count || storage.count <= 0) continue;

    if (!forKassa) {
      bitrixItems.push({
        itemType: 'storage',
        startDate: storage.startDate,
        finishDate: storage.endDate,
        sum: storage.price * storage.count,
        count: storage.count,
      });
    } else {
      const descriptionTemp = `Хранение спортивного инвентаря - ${MONTH_NAMES[storage.month]} ${storage.year}`;
      const priceTemp = testPayment(storage.price, phone);

      kassaItems.push({
        quantity: storage.count,
        amount: {
          value: parseFloat(priceTemp.toFixed(2)).toFixed(2),
          currency: 'RUB',
        },
        vat_code: 1,
        description: descriptionTemp,
        paymentMethodType: 'full_prepayment',
      });

      description.push(descriptionTemp);
      totalPrice += priceTemp * storage.count;
    }
  }

  const joinedDescription = description.join(' ');
  
  return {
    items: forKassa ? kassaItems : bitrixItems,
    hasGruntReOrder,
    description: joinedDescription.length < 128
      ? joinedDescription
      : joinedDescription.slice(0, 125) + '...',
    totalPrice
  };
}
